import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, Check, ChevronLeft, Compass, Loader2, MapPin, Search } from "lucide-react";
import { ACAO, defaultSelection, filtrarItens, fmtCoord, fmtSugestao, novosSemRegiao, origemRegiao, regiaoEfetiva, toAplicarPayload } from "./descobertaHelpers";

// Seletor de região reutilizado (padrão para todos + ajuste por bairro).
function RegiaoSelect({ value, onChange, regioes, placeholder = "Selecione…", testId, className = "" }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className={`h-8 text-xs ${className}`} data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{regioes.map((r) => <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>)}</SelectContent>
    </Select>
  );
}

const ORIGEM_LABEL = { sugerida: "sugerida pela posição", padrao: "região padrão", manual: "escolhida" };

// Chip "Zona Norte · 3,4 km" (região sugerida pela posição em relação ao centro da cidade).
function SugestaoChip({ sugestao, testId }) {
  if (!sugestao) return <span className="text-xs text-muted-foreground">—</span>;
  const ok = !!sugestao.regiao_id;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] ${ok ? "text-sky-800" : "text-amber-700"}`} title={ok ? "Região sugerida pela posição do bairro" : "Sem região cadastrada para esta zona"} data-testid={testId} data-zona={sugestao.zona}>
      <Compass className="w-3 h-3" />{ok ? sugestao.regiao_nome : `${sugestao.zona_label} (sem região)`}<span className="opacity-70">· {Number(sugestao.dist_km).toFixed(1).replace(".", ",")} km</span>
    </span>
  );
}

const ABAS = [["todos", "Todos"], ["criar", "Novos"], ["atualizar", "Atualizar"], ["igual", "Sem alteração"]];

function AcaoBadge({ acao }) {
  const a = ACAO[acao] || ACAO.igual;
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase whitespace-nowrap ${a.cls}`} data-acao={acao}>{a.label}</span>;
}

// Passo 1 — lista de bairros encontrados na cidade (selecionar/desmarcar individualmente).
function ListaStep({ itens, resumo, selected, setSelected }) {
  const [aba, setAba] = useState("todos");
  const [busca, setBusca] = useState("");
  const visiveis = useMemo(() => filtrarItens(itens, aba, busca), [itens, aba, busca]);
  const todosVisiveis = visiveis.length > 0 && visiveis.every((i) => selected.has(i.nome));
  const toggle = (nome) => setSelected((s) => { const n = new Set(s); if (n.has(nome)) n.delete(nome); else n.add(nome); return n; });
  const toggleVisiveis = () => setSelected((s) => {
    const n = new Set(s);
    visiveis.forEach((i) => (todosVisiveis ? n.delete(i.nome) : n.add(i.nome)));
    return n;
  });
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        {ABAS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setAba(k)} data-testid={`descoberta-aba-${k}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${aba === k ? "bg-orange-600 text-white border-orange-600" : "bg-white hover:bg-orange-50 border-slate-200"}`}>
            {label} <span className="opacity-70">({k === "todos" ? itens.length : resumo?.[k] ?? 0})</span>
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar bairro" className="pl-8 h-9 w-56" data-testid="descoberta-busca" />
        </div>
      </div>
      <div className="border rounded-lg overflow-auto max-h-[52vh]">
        <Table>
          <TableHeader>
            <TableRow className="bg-orange-50/60">
              <TableHead className="w-10"><input type="checkbox" checked={todosVisiveis} onChange={toggleVisiveis} className="accent-orange-600 w-4 h-4" data-testid="descoberta-chk-visiveis" /></TableHead>
              <TableHead>Bairro (OpenStreetMap)</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Coordenadas</TableHead>
              <TableHead>Região sugerida</TableHead>
              <TableHead>Situação no cadastro</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visiveis.map((i) => (
              <TableRow key={i.nome} data-testid={`descoberta-row-${i.nome}`} data-acao={i.acao} className={selected.has(i.nome) ? "bg-orange-50/40" : ""}>
                <TableCell><input type="checkbox" checked={selected.has(i.nome)} onChange={() => toggle(i.nome)} className="accent-orange-600 w-4 h-4" data-testid={`descoberta-chk-${i.nome}`} /></TableCell>
                <TableCell className="font-medium">{i.nome}{i.acao === "atualizar" && i.atual?.nome !== i.nome && <span className="block text-[11px] text-muted-foreground">cadastrado como “{i.atual?.nome}”</span>}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{i.tipo_label || "—"}</TableCell>
                <TableCell className="text-xs mono">{fmtCoord(i.lat, i.lng)}</TableCell>
                <TableCell><SugestaoChip sugestao={i.sugestao} testId={`descoberta-sugestao-${i.nome}`} /></TableCell>
                <TableCell><AcaoBadge acao={i.acao} />{(i.mudancas || []).includes("regiao") && <span className="block text-[10px] text-amber-700 mt-0.5">sem região → receberá a sugerida</span>}</TableCell>
              </TableRow>
            ))}
            {visiveis.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nenhum bairro nesta lista</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

// Passo 2 — preview: novos (lista com região) + atualizações em duas colunas (atual → novo).
function PreviewStep({ criar, atualizar, regioes, regiaoDefault, setRegiaoDefault, regiaoPorItem, setRegiaoPorItem }) {
  const nomeRegiao = (id) => regioes.find((r) => r.id === id)?.nome;
  const Diff = ({ a, b, children }) => <span className={a !== b ? "font-bold text-amber-800" : ""}>{children}</span>;
  const setItem = (nome, v) => setRegiaoPorItem((m) => ({ ...m, [nome]: v }));
  const comSugestao = criar.filter((i) => i.sugestao?.regiao_id).length;
  return (
    <div className="space-y-4 max-h-[58vh] overflow-auto pr-1" data-testid="descoberta-preview">
      {atualizar.length > 0 && (
        <div>
          <div className="text-sm font-bold text-amber-800 mb-2">Serão atualizados ({atualizar.length}) — revise antes de salvar</div>
          <div className="border border-amber-200 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-amber-50">
                  <TableHead>Bairro</TableHead>
                  <TableHead>Dado atual</TableHead>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Dado novo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {atualizar.map((i) => {
                  const mudaRegiao = (i.mudancas || []).includes("regiao");
                  return (
                    <TableRow key={i.nome} data-testid={`preview-upd-${i.nome}`}>
                      <TableCell className="font-medium">{i.nome}</TableCell>
                      <TableCell className="text-xs">
                        <div>{i.atual?.nome}</div>
                        <div className="mono text-muted-foreground">{fmtCoord(i.atual?.lat, i.atual?.lng)}</div>
                        {mudaRegiao && <div className="text-muted-foreground">Região: <span className="text-rose-700 font-semibold">sem região</span></div>}
                      </TableCell>
                      <TableCell><ArrowRight className="w-4 h-4 text-amber-600" /></TableCell>
                      <TableCell className="text-xs">
                        <Diff a={i.atual?.nome} b={i.nome}>{i.nome}</Diff>
                        <div className="mono"><Diff a={fmtCoord(i.atual?.lat, i.atual?.lng)} b={fmtCoord(i.lat, i.lng)}>{fmtCoord(i.lat, i.lng)}</Diff></div>
                        {mudaRegiao && (
                          <div className="flex items-center gap-2 mt-1">
                            <RegiaoSelect value={regiaoEfetiva(i, regiaoPorItem)} regioes={regioes} testId={`preview-upd-regiao-${i.nome}`} className="w-44 bg-white border-amber-300" onChange={(v) => setItem(i.nome, v)} />
                            <span className="text-[10px] text-sky-800">{ORIGEM_LABEL[origemRegiao(i, regiaoPorItem)]} · {fmtSugestao(i.sugestao)}</span>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      {criar.length > 0 && (
        <div>
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <div className="text-sm font-bold text-emerald-800">Serão criados ({criar.length})</div>
            {comSugestao > 0 && <span className="text-[11px] text-sky-800 inline-flex items-center gap-1" data-testid="preview-com-sugestao"><Compass className="w-3 h-3" />{comSugestao} com região sugerida pela posição</span>}
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs font-semibold text-slate-700">Região padrão {comSugestao < criar.length && <span className="text-rose-600">*</span>}</span>
              <RegiaoSelect value={regiaoDefault} onChange={setRegiaoDefault} regioes={regioes} placeholder="Para os sem sugestão" testId="descoberta-regiao-default" className="w-56 bg-white" />
            </div>
          </div>
          <div className="border border-emerald-200 rounded-lg overflow-hidden" data-testid="preview-criar">
            <Table>
              <TableHeader><TableRow className="bg-emerald-50"><TableHead>Bairro</TableHead><TableHead>Posição</TableHead><TableHead className="w-64">Região</TableHead></TableRow></TableHeader>
              <TableBody>
                {criar.map((i) => {
                  const efetiva = regiaoEfetiva(i, regiaoPorItem, regiaoDefault);
                  const origem = origemRegiao(i, regiaoPorItem, regiaoDefault);
                  return (
                    <TableRow key={i.nome} data-testid={`preview-new-${i.nome}`} data-regiao-origem={origem}>
                      <TableCell className="font-medium text-sm"><span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-emerald-700" />{i.nome}</span></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{i.sugestao ? fmtSugestao(i.sugestao) : "—"}</TableCell>
                      <TableCell>
                        <RegiaoSelect value={efetiva} regioes={regioes} testId={`preview-regiao-${i.nome}`}
                          placeholder="Sem região — escolha"
                          className={!efetiva ? "border-rose-300" : origem === "sugerida" ? "border-sky-300" : ""}
                          onChange={(v) => setItem(i.nome, v)} />
                        {efetiva && <span className="block text-[10px] text-muted-foreground mt-0.5">{ORIGEM_LABEL[origem]}{origem === "padrao" && regiaoDefault ? ` (${nomeRegiao(regiaoDefault)})` : ""}</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}

export function DescobertaDialog({ job, regioes = [], onClose, onAplicado }) {
  const itens = useMemo(() => job?.itens || [], [job]);
  const [selected, setSelected] = useState(() => defaultSelection(itens));
  const [step, setStep] = useState("lista");
  const [saving, setSaving] = useState(false);
  const [regiaoDefault, setRegiaoDefault] = useState("");
  const [regiaoPorItem, setRegiaoPorItem] = useState({});
  const escolhidos = useMemo(() => itens.filter((i) => selected.has(i.nome)), [itens, selected]);
  const criar = escolhidos.filter((i) => i.acao === "criar");
  const atualizar = escolhidos.filter((i) => i.acao === "atualizar");
  const efetivos = criar.length + atualizar.length;
  const semRegiao = novosSemRegiao(criar, regiaoPorItem, regiaoDefault);

  const aplicar = async () => {
    if (semRegiao.length) { toast.error(`Escolha a Região para ${semRegiao.length} bairro(s) novo(s)`); return; }
    setSaving(true);
    try {
      const r = await api.post("/bairros/descobrir/aplicar", { itens: toAplicarPayload(itens, selected, regiaoPorItem, regiaoDefault), regiao_id: regiaoDefault || null });
      toast.success(`${r.data.created} bairro(s) criado(s) · ${r.data.updated} atualizado(s)${r.data.skipped ? ` · ${r.data.skipped} ignorado(s)` : ""}`);
      onAplicado?.(r.data);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar bairros"); }
    finally { setSaving(false); }
  };

  const sug = job?.sugestao;
  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="descoberta-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MapPin className="w-5 h-5 text-orange-600" />Bairros encontrados em {job?.cidade_display || job?.cidade}</DialogTitle>
          <p className="text-xs text-muted-foreground" data-testid="descoberta-resumo">
            {itens.length} bairro(s) no OpenStreetMap · {job?.resumo?.criar ?? 0} novo(s) · {job?.resumo?.atualizar ?? 0} para atualizar · {job?.resumo?.igual ?? 0} sem alteração
          </p>
          {sug && (
            <p className="text-[11px] text-sky-800 flex items-center gap-1 flex-wrap" data-testid="descoberta-sugestao-info">
              <Compass className="w-3 h-3" />Região sugerida pela posição: até {Number(sug.raio_km).toFixed(0)} km do centro = Centro; depois, Norte/Sul/Leste/Oeste pela direção · {sug.com_regiao} bairro(s) com sugestão.
              {sug.zonas_sem_regiao?.length > 0 && <span className="text-amber-700">Sem região cadastrada para: {sug.zonas_sem_regiao.join(", ")}.</span>}
            </p>
          )}
        </DialogHeader>
        <div className="space-y-3 flex-1 min-h-0">
          {step === "lista"
            ? <ListaStep itens={itens} resumo={job?.resumo} selected={selected} setSelected={setSelected} />
            : <PreviewStep criar={criar} atualizar={atualizar} regioes={regioes} regiaoDefault={regiaoDefault} setRegiaoDefault={setRegiaoDefault} regiaoPorItem={regiaoPorItem} setRegiaoPorItem={setRegiaoPorItem} />}
        </div>
        <div className="flex items-center gap-2 pt-3 border-t">
          <span className="text-sm text-muted-foreground mr-auto" data-testid="descoberta-selecionados">
            <b>{escolhidos.length}</b> selecionado(s) — {criar.length} novo(s), {atualizar.length} atualização(ões){escolhidos.length - efetivos > 0 ? `, ${escolhidos.length - efetivos} sem alteração` : ""}
          </span>
          {step === "preview" && <Button variant="ghost" onClick={() => setStep("lista")} data-testid="descoberta-voltar"><ChevronLeft className="w-4 h-4 mr-1" />Voltar</Button>}
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          {step === "lista" ? (
            <Button onClick={() => setStep("preview")} disabled={efetivos === 0} className="bg-orange-600 hover:bg-orange-700" data-testid="descoberta-continuar">
              Revisar {efetivos} bairro(s)<ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={aplicar} disabled={saving || efetivos === 0 || semRegiao.length > 0} title={semRegiao.length ? "Escolha a região dos bairros novos" : ""} className="bg-emerald-600 hover:bg-emerald-700" data-testid="descoberta-confirmar">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}Confirmar e salvar
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
